/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '../../../shared/modals/Modal';
import { ActivityIndicator, Pressable, StyleSheet, Text, TextInput, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { colors } from '../../../shared/theme/colors'
import { spacing } from '../../../shared/theme/spacing';
import { styles } from '../posShared.styles';
import { modalExperience } from '../../../shared/modals/modalExperience.styles'
import { ModalFilterSelectTrigger } from '../../../shared/modals/ModalFilterSelectTrigger'
import { ModalFormField } from '../../../shared/modals/ModalFormField'
import { ModalNestedPicker } from '../../../shared/modals/ModalNestedPicker'
import type { ModalPickerOption } from '../../../shared/modals/ModalNestedPicker'
import { ModalScreenHeader } from '../../../shared/modals/ModalScreenHeader';
import { useModalBackdropPadding } from '../../../shared/ui/hooks-ui/useModalBackdropPadding';
import { DECIMAL_HINT_PRICE, parseDecimalInput } from '../../../shared/numeric/localeNumeric';
import { NumericTextInput } from '../../../shared/ui/NumericTextInput';
import type { RemoteBarcodeHit } from '../../../catalog/domain-api';
import { sourceLabels } from '../../../inventory/application/inventoryUtils';

export type PosBarcodeImportConfirmPayload = {
  name: string;
  price: number;
  categoryId: string;
};

type PosBarcodeImportModalProps = {
  visible: boolean;
  remoteHit: RemoteBarcodeHit | null;
  categoryOptions: ModalPickerOption[];
  defaultCategoryId: string | null;
  saving?: boolean;
  importError?: string | null;
  onClose: () => void;
  onConfirm: (payload: PosBarcodeImportConfirmPayload) => void;
};

function MobileKeyboardWrap({ enabled, children }: { enabled: boolean; children: React.ReactNode }) {
  if (!enabled) return <>{children}</>;
  return (
    <KeyboardAvoidingView behavior="padding" style={sheetStyles.keyboardWrap} automaticOffset>
      {children}
    </KeyboardAvoidingView>
  );
}

export const PosBarcodeImportModal = ({
  visible,
  remoteHit,
  categoryOptions,
  defaultCategoryId,
  saving = false,
  importError = null,
  onClose,
  onConfirm,
}: PosBarcodeImportModalProps) => {
  const modalBackdropPadding = useModalBackdropPadding();
  const { width, height: winH } = useWindowDimensions();
  const isMobile = width < 768;
  const insets = useSafeAreaInsets();
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [categoryPickerVisible, setCategoryPickerVisible] = useState(false);
  const [saveAttempted, setSaveAttempted] = useState(false);

  useEffect(() => {
    if (!visible || !remoteHit) {
      return;
    }
    setName(remoteHit.suggestion.name);
    setPrice('');
    setCategoryId(defaultCategoryId);
    setSaveAttempted(false);
  }, [visible, remoteHit, defaultCategoryId]);

  const trimmedName = name.trim();
  const trimmedPrice = price.trim();
  const parsedPrice = trimmedPrice === '' ? null : parseDecimalInput(price, { min: 0 });
  const priceInvalid = trimmedPrice.length === 0 || parsedPrice === null || parsedPrice <= 0;
  const categoryInvalid = !categoryId?.trim();
  const showPriceError = saveAttempted && priceInvalid;
  const showCategoryError = saveAttempted && categoryInvalid;

  const categoryLabel = useMemo(() => {
    if (!categoryId) return 'Elegir categoría';
    return categoryOptions.find((opt) => opt.value === categoryId)?.label ?? 'Categoría';
  }, [categoryId, categoryOptions]);

  const priceHelperText = useMemo(() => {
    if (!showPriceError) return DECIMAL_HINT_PRICE;
    if (trimmedPrice.length === 0) return 'Ingresá el precio de venta.';
    if (parsedPrice === null) return 'Ingresá un precio válido (ej. 1500 o 1500,50).';
    return 'El precio debe ser mayor a cero.';
  }, [parsedPrice, showPriceError, trimmedPrice.length]);

  const handleConfirmPress = () => {
    if (saving) return;
    if (priceInvalid || categoryInvalid || !trimmedName) {
      setSaveAttempted(true);
      return;
    }
    onConfirm({
      name: trimmedName,
      price: parsedPrice!,
      categoryId: categoryId!.trim(),
    });
  };

  if (!remoteHit) {
    return null;
  }

  const sourceLabel = sourceLabels[remoteHit.source] ?? remoteHit.source;

  return (
    <Modal visible={visible} transparent animationType={isMobile ? 'slide' : 'fade'} onRequestClose={saving ? undefined : onClose} accessibilityViewIsModal>
      <>
      <View style={[styles.modalBackdrop, isMobile ? { justifyContent: 'flex-end' } : modalExperience.modalRoot, isMobile ? null : modalBackdropPadding]}>
        <Pressable style={StyleSheet.absoluteFill} onPress={isMobile && !saving ? onClose : undefined} />
        <MobileKeyboardWrap enabled={isMobile}>
        <View
          testID="pos-barcode-import-modal"
          style={[isMobile ? [sheetStyles.sheetCard, { maxHeight: winH * 0.96, paddingBottom: Math.max(insets.bottom, spacing.sm) }] : [styles.modalCard, modalExperience.modalWidthStandard]]}
        >
          {isMobile ? <View style={sheetStyles.grabber} /> : null}
          <ModalScreenHeader
            onClose={onClose}
            title="Importar y agregar"
            subtitle={
              remoteHit.source === 'open_food_facts'
                ? 'Encontramos este producto por el código de barras en Open Food Facts. Revisá nombre y precio.'
                : `Encontramos una ficha por el código de barras (${sourceLabel}). Revisá nombre y precio.`
            }
            closeAccessibilityLabel="Cancelar importación"
          />

          <View style={[sheetStyles.scroll, modalExperience.formPanel]}>
            <View collapsable={false}>
              <ModalFormField label="Nombre" required>
                <TextInput
                  value={name}
                  onChangeText={setName}
                  placeholder="Nombre del producto"
                  placeholderTextColor={colors.textMuted}
                  style={modalExperience.formInput}
                  editable={!saving}
                />
              </ModalFormField>
              <ModalFormField label="Precio de venta" required>
                <NumericTextInput
                  kind="decimalMoney"
                  value={price}
                  onChangeText={setPrice}
                  placeholder="Ej: 1500 o 1500,50"
                  placeholderTextColor={colors.textMuted}
                  style={[
                    modalExperience.formInput,
                    showPriceError && { borderColor: colors.warning },
                  ]}
                  editable={!saving}
                />
                <Text
                  style={[
                    modalExperience.formLabel,
                    showPriceError && { color: colors.warning },
                  ]}
                >
                  {priceHelperText}
                </Text>
              </ModalFormField>
              <ModalFilterSelectTrigger
                label="Categoría"
                value={categoryLabel}
                onPress={() => setCategoryPickerVisible(true)}
                active={Boolean(categoryId)}
                accessibilityLabel="Elegir categoría del producto"
              />
              {showCategoryError ? (
                <Text style={[modalExperience.formLabel, { color: colors.warning }]}>
                  Elegí una categoría.
                </Text>
              ) : null}
            </View>
          </View>

          {importError ? (
            <View
              testID="pos-barcode-import-error"
              style={{
                backgroundColor: colors.surfaceSecondary,
                borderRadius: 8,
                paddingHorizontal: 12,
                paddingVertical: 10,
                marginBottom: 8,
                borderWidth: 1,
                borderColor: colors.danger,
              }}
            >
              <Text style={{ color: colors.danger, fontSize: 13, fontWeight: '500' }}>
                {importError}
              </Text>
            </View>
          ) : null}

          <View style={[styles.modalActions, modalExperience.footerActions]}>
            <TouchableOpacity
              style={[styles.secondaryButton, styles.modalButtonHalf]}
              onPress={onClose}
              disabled={saving}
              accessibilityRole="button"
              accessibilityLabel="Cancelar importación"
            >
              <Text style={styles.secondaryButtonText}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              testID="pos-barcode-import-confirm"
              style={[
                styles.posPrimaryCta,
                styles.modalButtonHalf,
                saving && { opacity: 0.6 },
              ]}
              onPress={handleConfirmPress}
              disabled={saving}
              accessibilityRole="button"
              accessibilityLabel="Importar y agregar"
            >
              {saving ? (
                <ActivityIndicator color={colors.textPrimary} />
              ) : (
                <Text style={styles.posPrimaryCtaText}>Importar y agregar</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
        </MobileKeyboardWrap>
      </View>

      <ModalNestedPicker
        visible={categoryPickerVisible}
        title="Categoría"
        options={categoryOptions}
        selectedValue={categoryId ?? ''}
        onSelect={(value) => {
          setCategoryId(value);
          setCategoryPickerVisible(false);
        }}
        onClose={() => setCategoryPickerVisible(false)}
      />
      </>
    </Modal>
  );
};

const sheetStyles = StyleSheet.create({
  keyboardWrap: {
    width: '100%',
    // Acota el KAV al backdrop: sin esto el maxHeight % del sheet no resuelve en web
    maxHeight: '100%',
    justifyContent: 'flex-end',
  },
  sheetCard: {
    width: '100%',
    maxHeight: '96%',
    backgroundColor: colors.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderTopWidth: 1,
    borderColor: colors.borderSoft,
    paddingTop: 8,
  },
  scroll: {
    flexShrink: 1,
  },
  grabber: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.borderSoft,
    alignSelf: 'center',
    marginBottom: spacing.xs,
  },
});
